// P5 app entry — the product: full-screen MapLibre basemap + multi-allergen
// pollen overlay (per-pixel MAX of selected allergens) + weekly timeline scrub
// (mouse + touch) over the manifest's slice count (52 weekly slices) +
// mobile-first floating UI (draggable bottom-sheet selector on phones, side
// panel on desktop) + shareable URL state + capability-probe hard gate.

import type { ErrorEvent } from "maplibre-gl";
import { createMap } from "./map";
import { probeCaps, decideMultiRegion, setCapCeilForTest } from "./caps";
import { RegionRegistry } from "./regionRegistry";
import { Timeline } from "./ui/timeline";
import { AllergenPanel } from "./ui/allergens";
import { Legend } from "./ui/legend";
import { AboutPanel } from "./ui/about";
import { AllergenSheet } from "./ui/sheet";
import { ConfidenceBadge, type BadgeZone } from "./ui/confidence-badge";
import { parseConfidence, isConfidenceTier } from "./confidence";
import { parseHash, UrlWriter, type ViewState } from "./urlState";

const root = document.getElementById("app")!;

// A lively mid-year default week (early June) so the map shows activity the
// moment an allergen is picked. Weekly index 22 ≈ "04 Jun – 10 Jun".
const DEFAULT_SLICE = 22;
const HAS_SELECTED_KEY = "pollen-has-selected";

function showFatal(msg: string, nSlices: number) {
  const el = document.createElement("div");
  el.className = "fatal";
  el.innerHTML =
    `<h1>This device can't run the pollen map</h1>` +
    `<p>${msg}</p>` +
    `<p class="fatal-sub">A WebGL2 GPU with at least 14 fragment texture ` +
    `units and ${nSlices}-layer array-texture support is required.</p>`;
  root.appendChild(el);
}

// M2 — a clear, full-page fatal for a data/load failure (not a blank page).
function showLoadError(title: string, msg: string) {
  const el = document.createElement("div");
  el.className = "fatal";
  el.innerHTML =
    `<h1>${title}</h1>` +
    `<p>${msg}</p>` +
    `<p class="fatal-sub">` +
    `<button type="button" class="reload-btn" ` +
    `onclick="location.reload()">Try again</button></p>`;
  root.appendChild(el);
}

// M2 — a non-fatal, dismissible banner for a DEGRADED state.
let noticeEl: HTMLElement | null = null;
function showNotice(msg: string) {
  if (!noticeEl) {
    noticeEl = document.createElement("div");
    noticeEl.className = "app-notice";
    noticeEl.setAttribute("role", "status");
    noticeEl.setAttribute("aria-live", "polite");
    root.appendChild(noticeEl);
  }
  noticeEl.innerHTML =
    `<span class="app-notice-text">${msg}</span>` +
    `<button type="button" class="app-notice-close" aria-label="Dismiss">` +
    `&times;</button>`;
  noticeEl
    .querySelector(".app-notice-close")
    ?.addEventListener("click", () => noticeEl?.remove());
}

async function boot() {
  // Parse any shared/deep-link view state up front so the map can be created at
  // the requested centre/zoom (no visible re-centre flash).
  const initial = parseHash();

  // --- Region registry: load EVERY region's manifest (Europe + North America
  // + Asia) and build the merged, code-deduplicated taxon list. Each region is
  // wired to its own PollenLayer (own geometry + GL resources) and rendered in
  // its own draw pass on the single map. The maxResident cap is set after the
  // caps probe, so we use a generous interim and re-cap is unnecessary (the
  // probe only depends on the slice count, which is 52 for all regions). ---
  let registry: RegionRegistry;
  try {
    registry = await RegionRegistry.load(12);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[pollen] region manifests load failed:", err);
    showLoadError(
      "Couldn't load the pollen data",
      "The map datasets (region manifests + pollen strips) failed to load. " +
        "This is usually a network issue or a missing/blocked " +
        "<code>data/</code> folder. Check your connection and try again.",
    );
    return;
  }
  // All regions are 52 weekly slices; the probe + timeline use the EU region's
  // count (the first/baseline region) as the canonical slice count.
  const baseRegion = registry.regions[0];
  const manifest = baseRegion.manifest;
  const nSlices = manifest.timeSlices.length;
  // eslint-disable-next-line no-console
  console.log(
    "[regions] loaded:",
    registry.regions.map((r) => `${r.key}(${r.manifest.allergens.length})`).join(", "),
    "| unified taxa:",
    registry.unified.length,
  );

  // --- HARD GATE: capability probe + residency/layout selection (Change A:
  // multi-region VRAM budgeting). The residency cap now accounts for ALL loaded
  // regions' REAL grids (Σ min(k,taxa)·nSlices·w·h) instead of pretending every
  // region is a single 600x800 region. For the current 3-region load this MUST
  // return the identical policy + cap as the old single-region decide(caps,52)
  // (NA/Asia are smaller than 600x800; the corrected sum never tightens the cap)
  // — asserted by the E0 non-regression harness. ---
  // Test-only residency ceiling hook (e.g. ?capCeil=5) to force a deeper LRU cap
  // than navigator.deviceMemory alone can reach, so the eviction path is
  // exercisable. No-op (undefined) in every normal run. R5: gated behind
  // import.meta.env.DEV so the URL param is INERT (and the call is UNREACHABLE)
  // in production builds — Vite statically replaces `import.meta.env.DEV` with
  // the literal `false` in `vite build` output, so the bundler drops this whole
  // branch and setCapCeilForTest can never be reached in prod. (dev/preview
  // harness runs with DEV=true keep the ?capCeil hook; the non-regression
  // harness's cap-eviction proof drives the cap via navigator.deviceMemory, not
  // this hook, so the prod gate does not affect it.) The `import.meta.env` type
  // comes from the local src/vite-env.d.ts (the repo's tsconfig sets
  // `"types": []`, so we declare the narrow ImportMeta shape ourselves).
  if (import.meta.env.DEV) {
    const capCeilParam = new URLSearchParams(location.search).get("capCeil");
    if (capCeilParam !== null) {
      const n = Number.parseInt(capCeilParam, 10);
      if (Number.isFinite(n) && n >= 1) setCapCeilForTest(n);
    }
  }
  // Change A — the historically-shipped baseline regions. These MUST keep the
  // old single-region decide() residency cap at EVERY device-memory value (the
  // user's #1 hard rule). decideMultiRegion absorbs the VRAM pressure of any
  // ADDED (non-baseline) region by capping/evicting the added regions FIRST, so
  // adding regions can never degrade EU/NA/East-Asia. A future region is "added"
  // automatically (it is not in this set) until/unless it is intentionally
  // promoted to baseline.
  const BASELINE_REGION_KEYS = new Set(["eu", "na", "asia"]);
  const regionGrids = registry.regions.map((r) => ({
    nSlices: r.layer.geometry.nSlices,
    w: r.layer.geometry.width,
    h: r.layer.geometry.height,
    taxaCount: r.manifest.allergens.length,
    baseline: BASELINE_REGION_KEYS.has(r.key),
  }));
  // A4: probe at the LARGEST region grid (the current max is EU's 600x800, so
  // this is identical to the old probe today) so a device that holds 600x800 but
  // not a larger future grid fails the gate cleanly instead of OOMing at runtime.
  const maxW = regionGrids.reduce((m, g) => Math.max(m, g.w), 600);
  const maxH = regionGrids.reduce((m, g) => Math.max(m, g.h), 800);
  const caps = probeCaps(nSlices, maxW, maxH);
  const decision = decideMultiRegion(caps, regionGrids);
  (window as unknown as { __pollenCaps: unknown }).__pollenCaps = caps;
  (window as unknown as { __pollenDecision: unknown }).__pollenDecision =
    decision;
  // eslint-disable-next-line no-console
  console.log("[caps] probe:", caps);
  // eslint-disable-next-line no-console
  console.log("[caps] decision:", decision);
  if (!decision.supported) {
    showFatal(decision.reason, nSlices);
    return;
  }
  // Apply the device-driven LRU residency cap PER region (each region keeps its
  // own resident pool). Baseline regions (EU/NA/East-Asia) get the unchanged
  // single-region decide() cap; only added regions are tightened under pressure.
  // For the existing-only load every entry equals decision.maxResidentAllergens,
  // so this is byte-identical to the old uniform setMaxResident.
  registry.setMaxResidentPerRegion(decision.perRegionCap);

  // --- Layout: full-screen map + floating controls ---
  const mapDiv = document.createElement("div");
  mapDiv.id = "map";
  root.appendChild(mapDiv);

  const legend = new Legend(manifest.palette.levels);

  const map = createMap(
    mapDiv,
    initial.lat !== undefined && initial.lng !== undefined
      ? { center: [initial.lng, initial.lat], zoom: initial.zoom }
      : { zoom: initial.zoom },
  );
  (window as unknown as { __pollenMap: unknown }).__pollenMap = map;

  let layersAdded = false;
  let mapLoaded = false;

  // --- M2: basemap / style load-failure handling ---
  let basemapNoticeShown = false;
  function noteBasemapTrouble() {
    if (basemapNoticeShown) return;
    basemapNoticeShown = true;
    showNotice(
      "The basemap couldn't be loaded. The map needs network access — the " +
        "OpenFreeMap basemap is fetched online. The pollen overlay and " +
        "controls may be limited until the connection is restored.",
    );
  }
  map.on("error", (e: ErrorEvent) => {
    // eslint-disable-next-line no-console
    console.error("[pollen] map error:", e?.error?.message ?? e);
    noteBasemapTrouble();
  });
  const loadWatchdog = window.setTimeout(() => {
    if (!mapLoaded) noteBasemapTrouble();
  }, 12000);

  // --- M1: WebGL context loss / restore handling ---
  const mapCanvas = map.getCanvas();
  mapCanvas.addEventListener(
    "webglcontextlost",
    (ev) => {
      ev.preventDefault();
      // eslint-disable-next-line no-console
      console.warn("[pollen] WebGL context lost — awaiting restore");
    },
    false,
  );
  mapCanvas.addEventListener(
    "webglcontextrestored",
    () => {
      // eslint-disable-next-line no-console
      console.warn("[pollen] WebGL context restored — re-creating overlays");
      if (!layersAdded) return;
      const doRestore = () => {
        try {
          // Re-add EVERY region layer on the fresh context, then re-upload each
          // region's retained mask + selected allergen arrays.
          for (const r of registry.regions) {
            if (map.getLayer(r.layerId)) map.removeLayer(r.layerId);
            map.addLayer(r.layer);
          }
          registry.restoreAfterContextLoss();
          map.triggerRepaint();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[pollen] overlay restore failed:", err);
          showNotice(
            "The map's graphics context was lost and couldn't be fully " +
              "restored. Reload the page if the overlay doesn't reappear.",
          );
        }
      };
      if (map.isStyleLoaded()) doRestore();
      else map.once("idle", doRestore);
    },
    false,
  );

  // About / info affordance (i button + modal). Change C — pass per-region
  // confidence info so the provenance section can become data-driven. When NO
  // region carries a `confidence` tier (the current EU/NA/Asia load), AboutPanel
  // renders the EXACT original hardcoded prose (byte-identical About-open).
  const about = new AboutPanel(
    registry.regions.map((r) => {
      const prov = r.manifest.provenance;
      // The worst-case tier present anywhere in the region (drives the honesty
      // note for regions whose dominant tier is fine but whose floor is not).
      const floorRaw = r.manifest.confidenceFloor;
      const floor = isConfidenceTier(floorRaw) ? floorRaw : undefined;
      return {
        key: r.key,
        label: r.label,
        tier: parseConfidence(r.manifest).tier,
        floor,
        // Region extent so the About panel can scope its honesty note +
        // satellite/required attributions to the VIEWED region (F3). Same bbox
        // the confidence badge keys on.
        bbox: r.manifest.geo?.bbox,
        provenance: prov
          ? {
              method:
                typeof prov.method === "string" ? prov.method : undefined,
              honesty:
                typeof prov.honesty === "string" ? prov.honesty : undefined,
              note: typeof prov.note === "string" ? prov.note : undefined,
              // `sources[]` carries the licence-required attributions + credits.
              sources: Array.isArray(prov.sources) ? prov.sources : undefined,
            }
          : undefined,
      };
    }),
  );
  mapDiv.appendChild(about.button);
  root.appendChild(about.overlay);

  // Loading toast shown while an allergen's strips decode + upload on first
  // selection (so the choice feels responsive on slower devices).
  const loadingToast = document.createElement("div");
  loadingToast.className = "loading-toast";
  loadingToast.hidden = true;
  loadingToast.setAttribute("role", "status");
  loadingToast.setAttribute("aria-live", "polite");
  loadingToast.innerHTML =
    `<span class="loading-spinner" aria-hidden="true"></span>` +
    `<span class="loading-text">Loading…</span>`;
  root.appendChild(loadingToast);
  const loadingText = loadingToast.querySelector<HTMLElement>(".loading-text")!;
  let pending = 0;

  map.on("load", async () => {
    mapLoaded = true;
    window.clearTimeout(loadWatchdog);

    // Add every region's layer (one custom layer / draw pass each) on the same
    // map. MapLibre draws them independently, so each draw only binds that
    // region's selected allergen arrays + its own mask (texture-unit safe).
    registry.addToMap(map);
    layersAdded = true;

    // Debug hook: ?paintAll=1 (production overlay behaviour unchanged otherwise).
    const dbg = new URLSearchParams(location.search);
    if (dbg.get("paintAll") === "1" || dbg.get("debug") === "paintall") {
      registry.setPaintAll(true);
    }

    // Decode + upload each region's transparency/land mask once (prefer hi-res).
    await registry.loadMasks();

    // The merged, code-deduplicated taxon list drives the selector. The panel
    // works by INDEX into this list; index<->code conversion is done here so the
    // selection stays region-agnostic (a code lights up in every region with it).
    const unified = registry.unifiedAsAllergens();
    const codeAt = (i: number): string => unified[i].code;
    const REGION_TAG: Record<string, string> = {
      eu: "EU",
      na: "NA",
      asia: "Asia",
      oceania: "AU+NZ",
      me: "ME",
      "asia-rest": "Asia+",
      latam: "LatAm",
      africa: "Africa",
    };

    // Forward declarations so the panel's onChange can reach the sheet + URL.
    let sheet: AllergenSheet;
    let urlWriter: UrlWriter | undefined;

    const allergenPanel = new AllergenPanel({
      allergens: unified,
      regionTags: (i) =>
        registry.regionsForCode(codeAt(i)).map((k) => REGION_TAG[k] ?? k),
      onBeforeSelect: async (i) => {
        const code = codeAt(i);
        pending++;
        loadingText.textContent = `Loading ${unified[i].english}…`;
        loadingToast.hidden = false;
        try {
          // Lazy-load this taxon's strips in EVERY region that carries it.
          await registry.ensureCodeDecoded(code);
        } finally {
          pending--;
          if (pending <= 0) {
            pending = 0;
            loadingToast.hidden = true;
          }
        }
      },
      onChange: (selected) => {
        // Translate the selected registry indices to codes and apply across all
        // regions; each region computes its own per-pixel MAX from its subset.
        const codes = selected.map(codeAt);
        registry.setSelectionByCodes(codes);
        sheet?.setSummary(selected.map((i) => unified[i].english));
        if (selected.length > 0) {
          try {
            localStorage.setItem(HAS_SELECTED_KEY, "1");
          } catch {
            /* private mode / storage disabled — non-fatal */
          }
        }
        urlWriter?.schedule();
      },
    });

    sheet = new AllergenSheet({
      grid: allergenPanel.el,
      legend: legend.el,
      onSelectAll: () => void allergenPanel.selectAll(),
      onClear: () => allergenPanel.clearAll(),
    });
    root.appendChild(sheet.el);

    const initialSlice = clampSlice(initial.week ?? DEFAULT_SLICE, nSlices);
    const timeline = new Timeline({
      slices: manifest.timeSlices,
      initial: initialSlice,
      onChange: (idx) => {
        registry.setSlice(idx);
        urlWriter?.schedule();
      },
    });
    registry.setSlice(initialSlice);

    const timelineSheet = document.createElement("div");
    timelineSheet.id = "timeline-sheet";
    timelineSheet.appendChild(timeline.el);
    root.appendChild(timelineSheet);

    // --- Change C: data-driven confidence badge (plan §4.3-C3). Parse each
    // region's top-level `confidence` STRING tier (undefined for EU/NA/Asia,
    // which ship none). The badge is ONLY mounted + wired when at least one
    // region carries a tier — so for the current tier-less load NOTHING is added
    // to the DOM and no moveend handler is attached => pixel-identical + no
    // badge. A QA-fail (mistyped value) is logged loudly by parseConfidence. ---
    const regionTierByKey: Record<string, string | undefined> = {};
    for (const r of registry.regions) {
      regionTierByKey[r.key] = parseConfidence(r.manifest).tier;
    }
    const confidenceBadge = new ConfidenceBadge(
      registry.regions.map((r) => ({
        key: r.key,
        label: r.label,
        bbox: r.manifest.geo?.bbox,
        tier: parseConfidence(r.manifest).tier,
        // Sub-area tier overrides (manifest.confidenceZones). Validated to the
        // 4-enum here; malformed/unknown zone tiers are dropped (region default
        // still shows). Absent on the 3 existing regions -> [] -> inert.
        zones: (r.manifest.confidenceZones ?? []).reduce<BadgeZone[]>(
          (acc, z) => {
            if (isConfidenceTier(z.confidence)) {
              acc.push({ bbox: z.bbox, tier: z.confidence, label: z.label });
            }
            return acc;
          },
          [],
        ),
      })),
    );
    if (confidenceBadge.hasAnyTier()) {
      mapDiv.appendChild(confidenceBadge.el);
      const refreshBadge = () => confidenceBadge.update(map.getCenter());
      map.on("moveend", refreshBadge);
      refreshBadge();
    }

    // Scope the About panel's honesty note + satellite/required attributions +
    // per-region credits to the region under the map center (F3). Updated on
    // every pan/zoom (cheap: a re-render only fires when the viewed region
    // changes) and once now so the panel is correct before the first move.
    const refreshAbout = () => about.update(map.getCenter());
    map.on("moveend", refreshAbout);
    refreshAbout();

    // --- Shareable URL state: encode the COMPLETE view (centre/zoom/week/
    // pollens-by-code), debounced, on every pan/zoom/scrub/selection. ---
    const getState = (): ViewState => {
      const c = map.getCenter();
      return {
        lat: c.lat,
        lng: c.lng,
        zoom: map.getZoom(),
        week: timeline.value,
        codes: allergenPanel.getSelectedCodes(),
      };
    };
    urlWriter = new UrlWriter(getState);
    map.on("moveend", () => urlWriter?.schedule());

    // --- Restore deep-link selection (by stable codes), else first-run state ---
    if (initial.codes && initial.codes.length > 0) {
      await allergenPanel.setSelectedByCodes(initial.codes);
      // A shared link with a selection: keep the sheet collapsed so the map is
      // the hero; the handle summarises the restored selection.
    } else {
      // First-run empty state (spec §3): no previous selection -> auto-expand
      // the sheet so the open sheet itself IS the prompt (no map overlay text).
      let firstRun = false;
      try {
        firstRun = localStorage.getItem(HAS_SELECTED_KEY) === null;
      } catch {
        firstRun = true;
      }
      if (firstRun) sheet.expand();
    }
    // Write the canonical URL once everything is settled.
    urlWriter.flush();

    panelExposeForE2E();
    function panelExposeForE2E() {
      (window as unknown as { __pollen: unknown }).__pollen = {
        ready: true,
        policy: decision.policy,
        maxResident: decision.maxResidentAllergens,
        setSelection: (idxs: number[]) => allergenPanel.setSelected(idxs),
        setSelectedByCodes: (codes: string[]) =>
          allergenPanel.setSelectedByCodes(codes),
        setSlice: (i: number) => {
          timeline.setIndex(i);
          registry.setSlice(i);
        },
        setPaletteMode: (m: 0 | 1) => registry.setPaletteMode(m),
        setPaintAll: (on: boolean) => registry.setPaintAll(on),
        setOpacity: (o: number) => registry.setOpacity(o),
        expandSheet: () => sheet.expand(),
        collapseSheet: () => sheet.collapse(),
        sliceCount: manifest.timeSlices.length,
        // Change C — per-region parsed confidence tier (E2E-only diagnostic;
        // renders nothing). Returns all-`undefined` for the current manifests so
        // the harness can assert "no confidence overlay where the manifest lacks
        // the field". Cross-checked against the harness's own manifest read.
        confidenceTierByRegion: (): Record<string, string | undefined> => ({
          ...regionTierByKey,
        }),
        // The merged taxon list (all codes across every region).
        allergens: unified.map((a) => a.code),
        // Region diagnostics for the QA harness (codes per region, layer ids).
        regions: registry.regions.map((r) => ({
          key: r.key,
          label: r.label,
          layerId: r.layerId,
          center: r.center,
          codes: r.manifest.allergens.map((a) => a.code),
          dims: r.layer.geometry,
        })),
      };
    }
    // eslint-disable-next-line no-console
    console.log("[pollen] P5 app ready:", decision.policy);
  });
}

function clampSlice(i: number, n: number): number {
  return Math.max(0, Math.min(n - 1, Math.round(i)));
}

void boot();
